import React, { Component } from 'react';
import Text from './Text';
import CM2 from '../images/cm2.png';
import DNZ from '../images/def_no_zoom.png';
import DP from '../images/def_p.png';

const FinalTDescription = <div id="Descript">


Welcome to the part 6 of the tutorial, here we will learn how to do REAL backtesting and in our case that means two things: retraining the model and adding trading costs.
Before proceeding I must warn you that this step can be computationally intensive and could take a long time depending on your hardware.<br/><br/>

As I said before, in this part I will only explain the sections of code that are significantly different from previous parts of the tutorial.
The full code used for every step and the final models can be found on my <a href="https://github.com/JRachadell/Bitcoin_Deep_Learning">github</a>.<br/><br/>

Earlier in the tutorial I talked about look-ahead bias, that means using data to train a model and then testing how accurate it is looking backwards.
To overcome this we will retrain the model every N timesteps and then test the trading strategy looking forward.
The lower N (days, weeks, months), the more models you will have to train. I will retrain the model every 50 days starting 200 days ago, 4 models in total.
The parameters I'll use are good but are not the best that I've found;
the results are still pretty good with these parameters and now you know that the model and the strategy can be improved.<br/><br/>

After opening, editing and transforming 'btc.csv' like in prior parts of the tutorial, we will run a for loop that will train the 4 models.
What this piece of code will do is the following: create a slice of the original dataframe removing the last 50, 100, 150 and 200 observations for every model.
With each slice of the original dataframe the models will be trained and saved in reverse order, it starts with model 4 (everything except the last 50 observations),
then model 3 (everything except the last 100 observations) and so on.
Each model is saved as Model_(number).json and the weights are saved as  Model_(number)_weights.h5. After running this code the 4 models are trained and saved.
If you are using pycharm or any other IDE, the models will be saved to the root directory of your project. If not, remember to add full paths to the files like in previous parts.<br/><br/>

Last time I used 12 observations, a batch size of 50, mean squared error and "nadam" optimizer. To remove a little of overfitting and speed up the process I will use 600 epochs per model.
The code below will take around 50 minutes to be ready (12~ minutes per model including 2 minutes of rest between them) using a GTX 1060;
without GPU be patient because it will take a lot more.
<br/><br/>

<code>
for i in range(1, 5):<br/>
&nbsp;&nbsp;&nbsp;&nbsp;df_slice = df[:-50*i]<br/>
&nbsp;&nbsp;&nbsp;&nbsp;Data = np.asarray(df_slice['Returns'])<br/>
&nbsp;&nbsp;&nbsp;&nbsp;Data = np.atleast_2d(Data)<br/>
&nbsp;&nbsp;&nbsp;&nbsp;Data = Data.T<br/>
&nbsp;&nbsp;&nbsp;&nbsp;X = np.atleast_3d(np.array([Data[start:start + Obs] for start in range(0, Data.shape[0] - Obs)]))<br/>
&nbsp;&nbsp;&nbsp;&nbsp;y = Data[Obs:]<br/>
&nbsp;&nbsp;&nbsp;&nbsp;model = Sequential()<br/>
&nbsp;&nbsp;&nbsp;&nbsp;model.add(LSTM(input_shape=(1,), input_dim=1, output_dim=20, return_sequences=True))<br/>
&nbsp;&nbsp;&nbsp;&nbsp;model.add(LSTM(input_shape=(1,), input_dim=1, output_dim=20, return_sequences=False))<br/>
&nbsp;&nbsp;&nbsp;&nbsp;model.add(Dense(1))<br/>
&nbsp;&nbsp;&nbsp;&nbsp;model.add(Activation('linear'))<br/>
&nbsp;&nbsp;&nbsp;&nbsp;model.compile(loss="mse", optimizer="nadam")<br/>
&nbsp;&nbsp;&nbsp;&nbsp;model.fit(X, y, epochs=600, batch_size=50, verbose=2, shuffle=False)<br/>
&nbsp;&nbsp;&nbsp;&nbsp;lossname = "Model_" + str(5-i) + "_loss.csv"<br/>
&nbsp;&nbsp;&nbsp;&nbsp;paramsname = "Model_" + str(5-i) + "_params.json"<br/>
&nbsp;&nbsp;&nbsp;&nbsp;name = "Model_" + str(5-i) + ".json"<br/>
&nbsp;&nbsp;&nbsp;&nbsp;h5name = "Model_" + str(5-i) + "_weights.h5"<br/>
&nbsp;&nbsp;&nbsp;&nbsp;pd.DataFrame(NN.history).to_csv(lossname)<br/>
&nbsp;&nbsp;&nbsp;&nbsp;model_params = json.dumps(NN.params)<br/>
&nbsp;&nbsp;&nbsp;&nbsp;with open(paramsname, "w") as json_file:<br/>
&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;json_file.write(model_params)<br/>
&nbsp;&nbsp;&nbsp;&nbsp;with open(name, "w") as json_file:<br/>
&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;json_file.write(model_json)<br/>
&nbsp;&nbsp;&nbsp;&nbsp;model.save_weights(h5name)<br/>
&nbsp;&nbsp;&nbsp;&nbsp;print "Done with model number", 5-i<br/>
&nbsp;&nbsp;&nbsp;&nbsp;time.sleep(120)<br/>
&nbsp;<br/>
</code>
<br/>

After the models are done you can close this python console and open a new one.
Now in a new console we will load all the models and use them to predict future values in sequence. Imagine this is our data:<br/><br/>

[first values] + [200-150] + [150-100] +[100-50] + [50]<br/><br/>

The first model will predict first values and last 200 to 150.<br/>
The second model will predict the last 150 to 100 and so on.<br/>
This means that we train each model with data up to certain point, use it to predict the next 50 values, and then repeat the process with the next model.
Only [first values] are predicted with look-ahead bias but don’t worry about them because we won’t use them to test the strategy.<br/><br/>

Open, edit and transform 'btc.csv' as always and then run the next script to load all the models in order and save them to a list called "loaded_models".
If you previously saved the models with different names or in a different folder remember to change it here.<br/><br/>

<code>
loaded_models = []<br/>
for i in range(1, 5):<br/>
&nbsp;&nbsp;&nbsp;&nbsp;name = "Model_" + str(i) + ".json"<br/>
&nbsp;&nbsp;&nbsp;&nbsp;h5name = "Model_" + str(i) + "_weights.h5"<br/>
&nbsp;&nbsp;&nbsp;&nbsp;json_file = open(name, 'r')<br/>
&nbsp;&nbsp;&nbsp;&nbsp;loaded_model_json = json_file.read()<br/>
&nbsp;&nbsp;&nbsp;&nbsp;json_file.close()<br/>
&nbsp;&nbsp;&nbsp;&nbsp;loaded_model = model_from_json(loaded_model_json)<br/>
&nbsp;&nbsp;&nbsp;&nbsp;loaded_model.load_weights(h5name)<br/>
&nbsp;&nbsp;&nbsp;&nbsp;loaded_models.append(loaded_model)<br/>
&nbsp;<br/>
</code><br/>

With this piece of code we will predict values using the first model.
As explained earlier, the first model will predict the "first values" (the ones that won't be used to test the strategy)
and the last 200 to 150. The results will be added in order to a list called Predictions.<br/><br/>

<code>
df_slice = df[:-150]<br/>
Predictions = [loaded_models[0].predict(np.asarray(df['Price'])[j-Obs:j].reshape(1, Obs, 1)) for j in range(Obs, len(df_slice))]<br/>
Predictions = [df['Price'].iloc[0]]*Obs + [val[0][0] for val in Predictions]
</code><br/><br/>

Now we proceed to make predictions with the rest of the models,
this for loop will use each model to predict the values for the next 50 days starting with the day right after the model was trained.<br/><br/>

<code>
for i in range(2, 5):<br/>
&nbsp;&nbsp;&nbsp;&nbsp;if i != 4:<br/>
&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;df_slice = df[:-50*(4-i)]<br/>
&nbsp;&nbsp;&nbsp;&nbsp;elif i == 4:<br/>
&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;df_slice = df<br/>
&nbsp;&nbsp;&nbsp;&nbsp;floor = len(df_slice)-50<br/>
&nbsp;&nbsp;&nbsp;&nbsp;roof = len(df_slice)<br/>
&nbsp;&nbsp;&nbsp;&nbsp;values = [loaded_models[i-1].predict(np.asarray(df_slice['Price'])[j-Obs:j].reshape(1, Obs, 1)) for j in range(floor, roof)]<br/>
&nbsp;&nbsp;&nbsp;&nbsp;Predictions = Predictions + [val[0][0] for val in values]<br/>
&nbsp;&nbsp;&nbsp;&nbsp;print i, floor, roof, roof-floor<br/>
&nbsp;<br/>
</code>
<br/>

The output should be<br/><br/>
<mark>
2 2551 2601 50<br/>
3 2601 2651 50<br/>
4 2651 2701 50
</mark>
<br/><br/>

This output means that the second model was used to predict observations 2551 to 2601,
model 3 2601 to 2651, model 4 2651 to 2701 and each one of them predicted 50 observations.
The first model is not printed because it was used outside the loop to predict the rest of the observations not predicted by these 3 models.
<br/><br/>

The dataframe for tests this time will be only the last 200 observations because those are the ones we predicted using the models without look-ahead bias.<br/><br/>
<code>
dff = df[-200:]
</code>
<br/><br/>

To visualize the confusion matrix and the % of total correct use the same code of part 5, here's my output:<br/><br/>

<mark>
% Total Correct: 44.0<br/>
[[24&nbsp;&nbsp;12&nbsp;&nbsp;24]<br/>
&nbsp;[13&nbsp;&nbsp;20&nbsp;&nbsp;18]<br/>
&nbsp;[27&nbsp;&nbsp;18&nbsp;&nbsp;44]]
</mark>
<br/><br/>

And here is using SKLearn's function:<br/><br/>

<img src={CM2} alt="CM"/>
<br/>

For a model without look-ahead bias this is pretty good,
it predicts correctly more large movements (24+20) than the ones that move against the trades (12+13) and a nice percentage of neutrals.<br/><br/>

After the 4 models predicted the values we need, proceed to add trading costs. For that we only need to change a few values inside the strategy function.
We will set trading costs at 0.5% for buy or sell. At a price of $10.000 per bitcoin, that’s $50 per transaction, or $100 per buy and sell.
The trading function would be just like before, but only changing the following when we buy or sell:<br/><br/>

When buying, we pay 0.5% more:<br/><br/>
<code>cash[i] = cash[i-1] - price[i]*1.005<br/></code>
and<br/>
<code>btc[i] = btc[i-1] + (cash[i-1] / (price[i]*1.005))</code>
<br/><br/>
When selling, we get 0.5% less<br/><br/>

<code>cash[i] = cash[i-1] + price[i]*0.995</code><br/>
and <br/>
<code>cash[i] = cash[i-1] + ((price[i]*0.995) * btc[i-1])</code>
<br/><br/>

Now let's run the strategy and see the results. We will use 3% for buy and sell as we did before.<br/><br/>

<code>
ret_pred(3.0, 3.0)
</code>
<br/><br/>

<mark>
Strategy vs BuyNHold: 29715.2779994
</mark>
<br/><br/>

The non-bias model is above buy and hold! Let's check the chart:<br/><br/>

<code>
plt.xlabel('Date')<br/>
plt.ylabel('US$')<br/>
plt.plot(dff.index, dff['BuyNHold'])<br/>
plt.plot(dff.index, dff['Strategy'])<br/>
plt.legend(shadow=True, fontsize='large')
</code>
<br/><br/>


<img src={DNZ} alt="DNZ"/>
<br/>


Not bad at all, to check how the strategy performed vs buy and hold in % terms run:<br/><br/>

<code>
plt.xlabel('Date')<br/>
plt.ylabel('%')<br/>
plt.plot(dff.index, dff['St_VS_BnH_P'], "b")<br/>
plt.legend(shadow=True, fontsize='large')
</code>
<br/><br/>

<img src={DP} alt="DP"/>
<br/>

The first months it performs better than buy and hold and then falls below for a few weeks (during the last peaks).
After the bitcoin price hits the top in december, the performance vs buy and hold improves a lot for the rest of the excercise.
<br/><br/>

In conclusion, during a bull market it performed pretty much like buy and hold (very hard to do better),
during the bear market it performed better than buy and hold and during the sideways market the performance has not varied a lot.
Also I most point out that this strategy is only below the performance of buy and hold briefly, and that's pretty impressive considering bitcoin's volatility and strong bull market.<br/><br/>

To finish I'll do one last test: try the trading strategy with different values for entry and exit points, the ones we used before are 3% for buying and selling.
Let's use all the values from 0% to 7%.<br/><br/>

<code>
ret_pred(7.0, 7.0)<br/>
ret_pred(6.0, 6.0)<br/>
ret_pred(5.0, 5.0)<br/>
ret_pred(4.0, 4.0)<br/>
ret_pred(3.0, 3.0)<br/>
ret_pred(2.0, 2.0)<br/>
ret_pred(1.0, 1.0)<br/>
ret_pred(0.0, 0.0)
</code>
<br/><br/>

<mark>
Strategy vs BuyNHold: 15568.4576103<br/>
Strategy vs BuyNHold: 14864.1219979<br/>
Strategy vs BuyNHold: 2275.9673744<br/>
Strategy vs BuyNHold: 29965.230983<br/>
Strategy vs BuyNHold: 29715.2779994<br/>
Strategy vs BuyNHold: 19623.5128092<br/>
Strategy vs BuyNHold: 16705.205473<br/>
Strategy vs BuyNHold: 22206.5157544
</mark>
<br/><br/>

Well, this is great news. Not only the model performs better than buy and hold using 3%, it performs better using any value (The best one was 4%).
This means that the model is pretty good at forecasting next day's big movements.
Remember that the performance can be improved if we use leverage or take short positions and if we invest the cash in bonds or an index instead of keeping it idle as metioned in Part 4.
<br/><br/>

For any comments or questions @ me on twitter at <a href="https://twitter.com/jsusran">@jsusran</a>,
I'm planning to publish the whole tutorial on Medium so people can make comments,
also here's the link with all the code used: <a href="https://github.com/JRachadell/Bitcoin_Deep_Learning">Bitcoin Deep Learning</a>. Don't
forget to add me on <a href="https://www.linkedin.com/in/jsusran">LinkedIn</a>. Also if you want
to check my website of oil data visualization here's the link: <a href="http://www.theoildb.com/">TheOilDB</a><br/><br/>

Hit me up for any comments, recommendations, doubts or if you create your own trading strategy using neural networks, I would like to see the results!

</div>;

class FinalTests extends Component {


  componentDidMount() {
    window.onscroll = function() {scrollFunction()};

    function scrollFunction() {
        if (document.body.scrollTop > 200 || document.documentElement.scrollTop > 200) {
            document.getElementById("myBtn").style.display = "block";
        } else {
            document.getElementById("myBtn").style.display = "none";
        }
    }

    function findPos(obj) {
      var curtop = 0;
      if (obj.offsetParent) {
        while (obj = obj.offsetParent) {
              curtop += obj.offsetTop;
          };
      return [curtop];
      }
    }

    document.getElementById("myBtn").onclick = function() {
      const   scrollHeight = window.scrollY,
              scrollStep = Math.PI / ( 1000 / 15 ),
              cosParameter = scrollHeight / 2;
      var     scrollCount = 0,
              scrollMargin,
              scrollInterval = setInterval( function() {
                  if ( window.scrollY > 10 ) {
                      scrollCount = scrollCount + 1;  
                      scrollMargin = cosParameter - cosParameter * Math.cos( scrollCount * scrollStep );
                      window.scrollTo( 0, ( scrollHeight - scrollMargin ) );
                  } 
                  else clearInterval(scrollInterval); 
              }, 15 );
    }

    const scrollHeight = window.scrollY - findPos(document.getElementById("myNavbar"))[0],
    scrollStep = Math.PI / ( 1000 / 15 ),
    cosParameter = scrollHeight / 2;
    var scrollCount = 0,
    scrollMargin,
    scrollInterval = setInterval( function() {
        if ( window.scrollY - findPos(document.getElementById("myNavbar"))[0] > 10 ) {
            scrollCount = scrollCount + 1;  
            scrollMargin = cosParameter - cosParameter * Math.cos( scrollCount * scrollStep );
            window.scrollTo( 0, ( scrollHeight - scrollMargin ) );
        } 
        else clearInterval(scrollInterval); 
    }, 15 );
  }

  render() {
    return (
        <div id="Data">
        <h1>Part 6: Final Tests and Backtesting</h1>
          <Text Searchable="Time" description={FinalTDescription}/>
        </div>
    );
  }
}

export default FinalTests;



// WEBPACK FOOTER //
// ./src/components/FinalTests.js